import ReactMarkdown from "react-markdown";
import { useEffect } from "react";
import type { SlotType, ZudokuConfig } from "zudoku";

import { slotInjectionPlugin } from "./plugins/slot-injection";

const basePath = process.env.ZUDOKU_PUBLIC_BASE_PATH ?? "";

// The docs version lives in the URL path, while the API group itself is mounted under the public base path. This is also hardcoded in pages directory structure and injection plugin code.
const apiVersion = "1.3";

// Vite requires a static glob string (path shape: {target}/{id}/{slot}.{md,mdx})
const injectionLoaders = import.meta.glob("./injections/*/*/*.{md,mdx}");

const slots: Record<string, SlotType> = {}

function HashScrollRestorer({ hash }: { hash: string }) {
  useEffect(() => {
    if (!hash) return;

    const id = decodeURIComponent(hash.slice(1));
    let frame = 0;

    const scrollToTarget = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ block: "start" });
      });
    };

    const observer = new MutationObserver(scrollToTarget);
    observer.observe(document.body, { childList: true, subtree: true });
    scrollToTarget();

    const retries = [250, 750, 1500].map((delay) =>
      window.setTimeout(scrollToTarget, delay),
    );
    const stop = window.setTimeout(() => observer.disconnect(), 2000);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      retries.forEach(window.clearTimeout);
      window.clearTimeout(stop);
    };
  }, [hash]);

  return null;
}

slots["layout-after-head"] = ({ location }) => (
  <HashScrollRestorer hash={location.hash} />
);

// We use slot to inject the banner message, because the default banner does not support parsing markdown.
if (process.env.ZUDOKU_PUBLIC_BANNER_MESSAGE) {
  const classes = "upcloud-banner-message text-sm font-medium px-4 py-2 w-full";

  slots["layout-before-head"] = (
    <div className={classes}>
      <ReactMarkdown>
        {process.env.ZUDOKU_PUBLIC_BANNER_MESSAGE}
      </ReactMarkdown>
    </div>
  );
}

const config: ZudokuConfig = {
  basePath,
  slots,
  search: {
    type: "pagefind",
  },
  navigation: [
    { label: "API Reference", type: "link", to: `/${apiVersion}`, display: "hide" },
  ],
  navigationRules: [
    // Add getting started page to the top of the menu. This can be moved into a separate catogory later when we more sub-pages.
    { type: "insert", match: "API Reference/Information", position: "before", items: [
      {
        type: "doc", file: `pages/${apiVersion}/getting-started.mdx`, path: `${apiVersion}/getting-started`, label: "Getting Started",
      },
    ]},
    // Rename info page to "API Catalog"
    { type: "modify", match: "API Reference/Information", set: { label: "UpCloud API" } },
    // Group icons
    { type: "modify", match: "API Reference/Server",           set: { icon: "rows-2" } },
    { type: "modify", match: "API Reference/Network",          set: { icon: "command" } },
    { type: "modify", match: "API Reference/Account & Access", set: { icon: "user" } },
    { type: "modify", match: "API Reference/Billing",          set: { icon: "wallet" } },
    // Ungrouped item icons
    { type: "modify", match: "API Reference/Container Registry", set: { icon: "container" } },
    { type: "modify", match: "API Reference/Block Storage",  set: { icon: "disc-album" } },
    { type: "modify", match: "API Reference/Object Storage", set: { icon: "archive" } },
    { type: "modify", match: "API Reference/File Storage",   set: { icon: "folder-closed" } },
    { type: "modify", match: "API Reference/Database",      set: { icon: "database" } },
    { type: "modify", match: "API Reference/Load balancer", set: { icon: "network" } },
    { type: "modify", match: "API Reference/Kubernetes",    set: { icon: "ship-wheel" } },
    { type: "modify", match: "API Reference/Partner",       set: { icon: "handshake" } },
    { type: "modify", match: "API Reference/Zone",       set: { icon: "map-pinned" } },
    // Ordering. Each move inserts immediately before the anchor, so list items in display order.
    { type: "move", match: "API Reference/Block Storage",  to: "API Reference/Network", position: "before" },
    { type: "move", match: "API Reference/Object Storage", to: "API Reference/Network", position: "before" },
    { type: "move", match: "API Reference/File Storage",   to: "API Reference/Network", position: "before" },
    { type: "move", match: "API Reference/Database",      to: "API Reference/Account & Access", position: "before" },
    { type: "move", match: "API Reference/Load balancer", to: "API Reference/Account & Access", position: "before" },
    { type: "move", match: "API Reference/Kubernetes",    to: "API Reference/Account & Access", position: "before" },
    { type: "move", match: "API Reference/Container Registry", to: "API Reference/Account & Access", position: "before" },
    { type: "move", match: "API Reference/Partner",       to: "API Reference/Billing",          position: "before" },
  ],
  header: {
    navigation: [
      { type: "link", to: "https://hub.upcloud.com/login", label: "OPEN HUB", target: "_blank" },
      { type: "link", to: "https://signup.upcloud.com/?promo=deploy250", label: "TRY FOR FREE", target: "_blank" },
    ],
    placements: {
      navigation: "end",
    },
  },
  redirects: [{ from: "/", to: `/${apiVersion}/getting-started` }],
  apis: {
    type: "file",
    // Zudoku appends both the API path and schema version to download URLs. The root schema path
    // keeps pages under /1.3
    input: [{ path: "/", label: apiVersion, input: "./openapi.json" }],
    path: apiVersion,
    options: {
      disablePlayground: true,
      expandAllTags: false,
      examplesLanguage: "go",
      schemaDownload: {
        enabled: true,
        fileName: "upcloud-openapi",
      },
      supportedLanguages: [
        { value: "shell", label: "cURL" },
      ],
    },
  },
  metadata: {
    favicon: "/favicon.svg",
    title: "%s | UpCloud Developer Documentation",
    defaultTitle: "UpCloud Developer Documentation",
  },
  site: {
    title: "UpCloud API Documentation",
    logo: {
      src: {
        light: "/upcloud-logo-black.svg",
        dark: "/upcloud-logo-white.svg",
      },
      alt: "UpCloud logo",
    },
    footer: {
      position: "center",
      copyright: `© UpCloud Ltd ${new Date().getFullYear()}`,
      columns: [
        {
          title: "Resources",
          links: [
            { label: "Blog", href: "https://upcloud.com/blog/", target: "_blank" },
            { label: "Documentation", href: "https://upcloud.com/docs/", target: "_blank" },
            { label: "Tutorials", href: "https://upcloud.com/resources/tutorials/", target: "_blank" },
          ],
        },
        {
          title: "Company",
          links: [
            { label: "About Us", href: "https://upcloud.com/about/", target: "_blank" },
            { label: "Pricing", href: "https://upcloud.com/pricing/", target: "_blank" },
            { label: "Pricing Calculator", href: "https://calc.upcloud.com/cloud-servers?currency=EUR", target: "_blank" },
          ],
        },
        {
          title: "Legal",
          links: [
            { label: "Terms of Service", href: "https://upcloud.com/terms-of-service/", target: "_blank" },
            { label: "Privacy Notice", href: "https://upcloud.com/privacy-notice/", target: "_blank" },
            { label: "Cookie Policy", href: "https://upcloud.com/cookie-policy/", target: "_blank" },
          ],
        },
      ],
      social: [
        { icon: "github", href: "https://github.com/UpCloudLtd" },
        { icon: "youtube", href: "https://www.youtube.com/channel/UCkIkxF5pBTBLYgYHXDIUUuw" },
        { icon: "instagram", href: "https://www.instagram.com/upcloud/" },
        { icon: "facebook", href: "https://www.facebook.com/UpCloudLtd" },
      ],
    },
  },
  theme: {
    customCss: `
      .upcloud-banner-message {
        background-color: #7b00ff;
        color: #ffffff;
        text-align: center;
      }

      .upcloud-banner-message a {
        color: currentColor;
        text-decoration: underline;
      }

      header img[alt="UpCloud logo"] {
        max-height: 1.75rem;
      }

      ::selection {
        background-color: #d6b3ff;
        color: #000000;
      }

      .group\\/card {
        --tw-ring-color: transparent;
        border: 1px solid var(--border);
      }

      /* Hide the "On this page" sidecar/TOC on the Schemas page only.
         Targets the aside that contains schema-anchor links (/${apiVersion}/~schemas#...). */
      aside:has(a[href*="/~schemas#"]) {
        display: none;
      }

      /* Header "LOGIN" link as an outline button */
      header a[href="https://hub.upcloud.com/login"] {
        background-color: transparent;
        color: var(--foreground);
        border: 1px solid var(--border);
        padding: 0.5rem 1rem;
        border-radius: 0.375rem;
        font-weight: 500;
        transition: background-color 0.15s ease, border-color 0.15s ease;
      }
      header a[href="https://hub.upcloud.com/login"]:hover {
        background-color: var(--muted);
        border-color: #7b00ff;
      }

      /* Header "TRY FOR FREE" link as a solid button */
      header a[href="https://signup.upcloud.com/?promo=deploy250"] {
        background-color: #7b00ff;
        color: #ffffff;
        padding: 0.5rem 1rem;
        border-radius: 0.375rem;
        font-weight: 500;
        transition: background-color 0.15s ease;
      }
      header a[href="https://signup.upcloud.com/?promo=deploy250"]:hover {
        background-color: #9533ff;
      }

      /* Tune injected callout spacing */
      div.injected__content-before div[role=note] {
        margin-top: calc(var(--spacing) * 6);
        margin-bottom: 0;
      }
    `,
    /* Other possible customizations:
       - border-radius: the border radiuses used by default are quite a lot larger than what we use in e.g. hub. However, seems that radius theme option or --radius-XX CSS variables are not used everywhere, so customizing does not really work at the moment.
    */
    light: {
      background: "#ffffff",
      foreground: "#0c001a",
      primary: "#7b00ff",
      primaryForeground: "#ffffff",
      secondary: "#f8f2ff",
      secondaryForeground: "#0c001a",
      muted: "#f8f2ff",
      mutedForeground: "#545454",
      accent: "#f8f2ff",
      accentForeground: "#0c001a",
      border: "#e8e8e8",
      ring: "#7b00ff",
    },
    dark: {
      background: "#0c001a",
      foreground: "#ffffff",
      card: "#0c001a",
      cardForeground: "#ffffff",
      primary: "#ca99ff",
      primaryForeground: "#0c001a",
      secondary: "#250f3e",
      secondaryForeground: "#ffffff",
      muted: "#180a29",
      mutedForeground: "#a3a3a3",
      accent: "#250f3e",
      accentForeground: "#ffffff",
      border: "#545454",
      ring: "#ca99ff",
    },
  },
  syntaxHighlighting: {
    themes: {
      light: "github-light",
      // The default dark theme has quite light background color, so use one that matches our dark theme better.
      dark: "tokyo-night",
    },
  },
  plugins: [slotInjectionPlugin({ loaders: injectionLoaders })],
};

export default config;
